/**
 * dashboardDemo.ts — Datos de DEMOSTRACIÓN para previsualizar la Home sin
 * backend ni sesión. SOLO se usan en desarrollo y detrás de `?preview` (ver
 * Dashboard). NUNCA se muestran a un usuario real: en producción la Home
 * consume los endpoints reales y, sin datos, enseña estados vacíos.
 *
 * Sirven al bucle de fidelidad visual (capturar la Home poblada) y como
 * vista previa de desarrollo. Los números son ilustrativos, deterministas.
 */

import type {
  AccountQuota,
  InboxItem,
  UserJobSummary,
  PipelineCard,
  PipelineStage,
  NextStepStatus,
  ActivityItem,
  BusinessReview,
  ReviewJob,
  SalesAngle,
} from '@/services/listingsService'
import type { SubscriptionPlan } from '@/types'

const DAY = 24 * 60 * 60 * 1000
function daysAgoIso(d: number): string {
  return new Date(Date.now() - d * DAY).toISOString()
}

/** Coordenadas WGS84 reales de los municipios usados en el demo. */
const CITY_COORDS: Record<string, [number, number]> = {
  'A Coruña': [43.3623, -8.4115],
  Vigo: [42.2406, -8.7207],
  Santiago: [42.8782, -8.5448],
  Ferrol: [43.484, -8.233],
  Narón: [43.504, -8.176],
  Lugo: [43.0097, -7.5567],
  Ourense: [42.3358, -7.8639],
  Fene: [43.454, -8.161],
  Betanzos: [43.28, -8.211],
  Pontevedra: [42.431, -8.6444],
}

/** Jitter determinista (±~0.012º) a partir del place_id para que los leads de
 *  una misma ciudad no caigan exactamente en el mismo punto. */
function jitter(seed: string, axis: number): number {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i) + axis * 7) | 0
  return ((h % 1000) / 1000 - 0.5) * 0.024
}

/** Hash entero determinista a partir de un string (datos demo estables). */
function jitterInt(seed: string): number {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  return h
}

/** Código postal demo determinista por ciudad (4 dígitos provincia + sufijo). */
const CITY_ZIP: Record<string, string> = {
  'A Coruña': '15001', Santiago: '15703', Vigo: '36201', Lugo: '27001',
  Ferrol: '15401', Narón: '15570', Ourense: '32003', Fene: '15500',
  Betanzos: '15300', Pontevedra: '36001',
}
const DEMO_STREETS = ['Rúa Real', 'Avda. de Galicia', 'Rúa do Franco', 'Praza Maior', 'Rúa Nova', 'Camiño Novo']

/** Crea un InboxItem completo a partir de los campos relevantes (resto null). */
function item(
  p: {
    place_id: string
    title: string
    category_primary: string
    city: string
    rating_value: number | null
    rating_votes_count: number | null
    url: string | null
    is_claimed: boolean | null
    email_primary: string | null
    phone: string | null
    daysAgo: number
  },
): InboxItem {
  // Datos del negocio deterministas para que la ficha (sección "Datos del
  // negocio") se vea representativa en el bucle de fidelidad.
  const seed = Math.abs(jitterInt(p.place_id))
  const zip = CITY_ZIP[p.city] ?? null
  const street = `${DEMO_STREETS[seed % DEMO_STREETS.length]}, ${(seed % 80) + 1}`
  const address_full = `${street} · ${p.city}${zip ? ` ${zip}` : ''}`
  const total_photos = 8 + (seed % 120)
  return {
    place_id: p.place_id,
    title: p.title,
    original_title: null,
    description: null,
    category_primary: p.category_primary,
    category_ids: null,
    additional_categories: null,
    address_full,
    address_components: { city: p.city, region: 'Galicia', country_code: 'ES', zip },
    phone: p.phone,
    phones_extra: null,
    url: p.url,
    domain: p.url,
    email_primary: p.email_primary,
    emails_extra: null,
    logo: null,
    main_image: null,
    total_photos,
    is_claimed: p.is_claimed,
    rating_value: p.rating_value,
    rating_votes_count: p.rating_votes_count,
    rating_distribution: null,
    price_level: null,
    current_status: 'open',
    work_hours: null,
    attributes: null,
    place_topics: null,
    check_url: null,
    cid: null,
    feature_id: null,
    latitude: CITY_COORDS[p.city] ? CITY_COORDS[p.city][0] + jitter(p.place_id, 0) : null,
    longitude: CITY_COORDS[p.city] ? CITY_COORDS[p.city][1] + jitter(p.place_id, 1) : null,
    first_extracted_at: daysAgoIso(p.daysAgo),
    first_job_id: 'demo-job',
    jobs_count: 1,
  }
}

// Plantillas con variedad para producir agregados realistas (calidad de base,
// categorías, zonas, valoración, oportunidades del día).
export const DEMO_INBOX: InboxItem[] = [
  // Oportunidades altas y recientes (fuerte pero carente)
  item({ place_id: 'd1', title: 'Marisquería O Porto', category_primary: 'restaurant', city: 'A Coruña', rating_value: 4.8, rating_votes_count: 96, url: null, is_claimed: false, email_primary: null, phone: '+34 981 22 14 50', daysAgo: 0 }),
  item({ place_id: 'd2', title: 'Clínica Dental Sonría', category_primary: 'dentist', city: 'Vigo', rating_value: 4.9, rating_votes_count: 11, url: null, is_claimed: false, email_primary: 'hola@sonria.es', phone: '+34 986 11 23 40', daysAgo: 0 }),
  item({ place_id: 'd3', title: 'Taller Hnos. Castro', category_primary: 'auto_repair_shop', city: 'Santiago', rating_value: 4.6, rating_votes_count: 42, url: null, is_claimed: true, email_primary: null, phone: '+34 981 55 70 12', daysAgo: 1 }),
  item({ place_id: 'd4', title: 'Lex Galicia Asesores', category_primary: 'lawyer', city: 'Narón', rating_value: 4.9, rating_votes_count: 203, url: 'lexgalicia.es', is_claimed: false, email_primary: null, phone: '+34 981 38 19 02', daysAgo: 1 }),
  item({ place_id: 'd5', title: 'Panadería A Pedra', category_primary: 'bakery', city: 'Ferrol', rating_value: 4.7, rating_votes_count: 8, url: null, is_claimed: false, email_primary: null, phone: '+34 981 30 88 21', daysAgo: 2 }),

  // Restaurantes
  item({ place_id: 'r1', title: 'Casa Beiro', category_primary: 'restaurant', city: 'A Coruña', rating_value: 4.5, rating_votes_count: 220, url: 'casabeiro.com', is_claimed: true, email_primary: 'info@casabeiro.com', phone: '+34 981 20 11 02', daysAgo: 3 }),
  item({ place_id: 'r2', title: 'O Forno Vello', category_primary: 'restaurant', city: 'Santiago', rating_value: 4.2, rating_votes_count: 64, url: 'fornovello.es', is_claimed: true, email_primary: 'reservas@fornovello.es', phone: '+34 981 57 33 19', daysAgo: 4 }),
  item({ place_id: 'r3', title: 'A Taberna do Mar', category_primary: 'restaurant', city: 'Vigo', rating_value: 4.6, rating_votes_count: 130, url: null, is_claimed: false, email_primary: null, phone: '+34 986 43 90 55', daysAgo: 5 }),
  item({ place_id: 'r4', title: 'Mesón Carballeira', category_primary: 'restaurant', city: 'Lugo', rating_value: 3.9, rating_votes_count: 41, url: 'carballeira.gal', is_claimed: true, email_primary: 'meson@carballeira.gal', phone: '+34 982 22 14 70', daysAgo: 6 }),
  item({ place_id: 'r5', title: 'Pulpería Recuncho', category_primary: 'restaurant', city: 'A Coruña', rating_value: 4.4, rating_votes_count: 88, url: null, is_claimed: true, email_primary: null, phone: '+34 981 26 70 33', daysAgo: 7 }),

  // Clínicas
  item({ place_id: 'c1', title: 'Clínica Vida', category_primary: 'dentist', city: 'A Coruña', rating_value: 4.7, rating_votes_count: 156, url: 'clinicavida.es', is_claimed: true, email_primary: 'cita@clinicavida.es', phone: '+34 981 14 50 60', daysAgo: 8 }),
  item({ place_id: 'c2', title: 'Fisioterapia Atlántico', category_primary: 'physiotherapist', city: 'Vigo', rating_value: 4.8, rating_votes_count: 73, url: null, is_claimed: false, email_primary: 'info@fisioatlantico.es', phone: '+34 986 22 18 90', daysAgo: 9 }),
  item({ place_id: 'c3', title: 'Centro Médico Sar', category_primary: 'doctor', city: 'Santiago', rating_value: 4.3, rating_votes_count: 49, url: 'centrosar.es', is_claimed: true, email_primary: null, phone: '+34 981 59 22 11', daysAgo: 11 }),
  item({ place_id: 'c4', title: 'Óptica Mariña', category_primary: 'optician', city: 'Ferrol', rating_value: 4.6, rating_votes_count: 31, url: null, is_claimed: false, email_primary: null, phone: '+34 981 35 12 88', daysAgo: 12 }),
  item({ place_id: 'c5', title: 'Dental Breogán', category_primary: 'dentist', city: 'A Coruña', rating_value: 4.9, rating_votes_count: 14, url: null, is_claimed: false, email_primary: 'hola@dentalbreogan.es', phone: '+34 981 90 11 23', daysAgo: 13 }),

  // Talleres
  item({ place_id: 't1', title: 'Talleres Rial', category_primary: 'auto_repair_shop', city: 'Vigo', rating_value: 4.4, rating_votes_count: 58, url: 'talleresrial.com', is_claimed: true, email_primary: 'taller@rial.com', phone: '+34 986 27 44 10', daysAgo: 14 }),
  item({ place_id: 't2', title: 'Neumáticos Coruña', category_primary: 'tire_shop', city: 'A Coruña', rating_value: 4.1, rating_votes_count: 22, url: null, is_claimed: false, email_primary: null, phone: '+34 981 13 66 70', daysAgo: 16 }),
  item({ place_id: 't3', title: 'Auto Lema', category_primary: 'auto_repair_shop', city: 'Santiago', rating_value: 4.7, rating_votes_count: 9, url: null, is_claimed: false, email_primary: null, phone: '+34 981 58 02 47', daysAgo: 18 }),
  item({ place_id: 't4', title: 'Chapa y Pintura Sil', category_primary: 'auto_body_shop', city: 'Lugo', rating_value: 4.5, rating_votes_count: 37, url: 'chapasil.es', is_claimed: true, email_primary: 'info@chapasil.es', phone: '+34 982 25 30 14', daysAgo: 20 }),

  // Abogados
  item({ place_id: 'a1', title: 'Fontán Abogados', category_primary: 'lawyer', city: 'Fene', rating_value: 4.7, rating_votes_count: 77, url: 'fontan.legal', is_claimed: true, email_primary: 'info@fontan.legal', phone: '+34 981 34 06 23', daysAgo: 21 }),
  item({ place_id: 'a2', title: 'Asesoría Breogán', category_primary: 'lawyer', city: 'Ferrol', rating_value: 4.5, rating_votes_count: 34, url: 'breoganlegal.es', is_claimed: false, email_primary: null, phone: '+34 981 31 45 19', daysAgo: 22 }),
  item({ place_id: 'a3', title: 'Bufete Cardama', category_primary: 'lawyer', city: 'Ferrol', rating_value: 4.8, rating_votes_count: 126, url: 'cardama.es', is_claimed: true, email_primary: 'info@cardama.es', phone: '+34 981 35 22 10', daysAgo: 24 }),

  // Inmobiliarias
  item({ place_id: 'i1', title: 'Inmobiliaria Atlántica', category_primary: 'real_estate_agency', city: 'Vigo', rating_value: 4.2, rating_votes_count: 45, url: 'inmoatlantica.es', is_claimed: true, email_primary: 'ventas@inmoatlantica.es', phone: '+34 986 19 27 33', daysAgo: 26 }),
  item({ place_id: 'i2', title: 'Fincas Lugo', category_primary: 'real_estate_agency', city: 'Lugo', rating_value: 4.0, rating_votes_count: 12, url: null, is_claimed: false, email_primary: null, phone: '+34 982 21 88 04', daysAgo: 28 }),

  // Otras
  item({ place_id: 'o1', title: 'Peluquería Estilo', category_primary: 'hair_salon', city: 'Ourense', rating_value: 4.6, rating_votes_count: 53, url: null, is_claimed: false, email_primary: null, phone: '+34 988 22 10 45', daysAgo: 29 }),
  item({ place_id: 'o2', title: 'Floristería Camelia', category_primary: 'florist', city: 'Ourense', rating_value: 4.9, rating_votes_count: 18, url: 'camelia.gal', is_claimed: true, email_primary: 'pedidos@camelia.gal', phone: '+34 988 37 11 92', daysAgo: 30 }),
  item({ place_id: 'o3', title: 'Gimnasio Forma', category_primary: 'gym', city: 'A Coruña', rating_value: 4.3, rating_votes_count: 91, url: 'gimnasioforma.es', is_claimed: true, email_primary: 'info@gimnasioforma.es', phone: '+34 981 28 50 19', daysAgo: 31 }),
]

/**
 * Dataset GRANDE y disperso (cientos de puntos por toda España) SOLO para el
 * bucle de fidelidad del mapa (`?mapcase=big`): verifica rendimiento de los
 * pines y que a vista país se vea el volumen (clustering ligero) rompiéndose en
 * pines de punta fina a nivel región. Determinista (sin Math.random) para que
 * las capturas sean reproducibles.
 */
const BIG_HUBS: { city: string; lat: number; lng: number; n: number }[] = [
  { city: 'A Coruña', lat: 43.36, lng: -8.41, n: 20 },
  { city: 'Santiago', lat: 42.88, lng: -8.54, n: 14 },
  { city: 'Vigo', lat: 42.23, lng: -8.72, n: 16 },
  { city: 'Lugo', lat: 43.01, lng: -7.55, n: 12 },
  { city: 'Madrid', lat: 40.42, lng: -3.70, n: 22 },
  { city: 'Barcelona', lat: 41.39, lng: 2.17, n: 18 },
  { city: 'València', lat: 39.47, lng: -0.38, n: 14 },
  { city: 'Sevilla', lat: 37.39, lng: -5.99, n: 14 },
  { city: 'Bilbao', lat: 43.26, lng: -2.93, n: 12 },
  { city: 'Valladolid', lat: 41.65, lng: -4.72, n: 10 },
  { city: 'Zaragoza', lat: 41.65, lng: -0.89, n: 12 },
  { city: 'Málaga', lat: 36.72, lng: -4.42, n: 12 },
  { city: 'Las Palmas', lat: 28.12, lng: -15.44, n: 14 },
]

let BIG_CACHE: InboxItem[] | null = null
function bigDemoInbox(): InboxItem[] {
  if (BIG_CACHE) return BIG_CACHE
  const cats = ['restaurant', 'dentist', 'lawyer', 'auto_repair_shop', 'bakery', 'gym', 'hair_salon', 'real_estate_agency']
  const out: InboxItem[] = []
  let n = 0
  for (const hub of BIG_HUBS) {
    for (let k = 0; k < hub.n; k++) {
      const id = `big-${n}`
      // Dispersión ~±0.2° (~22 km) alrededor del núcleo provincial: refleja el
      // reparto real por la provincia y deja ver el volumen como pines sueltos.
      const lat = hub.lat + jitter(id, 0) * 16
      const lng = hub.lng + jitter(id, 1) * 16
      // Variedad de banda de oportunidad ciclando reputación/carencias.
      const mod = n % 3
      const it = item({
        place_id: id,
        title: `${hub.city} · negocio ${k + 1}`,
        category_primary: cats[n % cats.length],
        city: hub.city,
        rating_value: mod === 0 ? 4.8 : mod === 1 ? 4.3 : 3.6,
        rating_votes_count: mod === 0 ? 180 : mod === 1 ? 60 : 14,
        url: mod === 0 ? null : 'ejemplo.es',
        is_claimed: mod === 0 ? false : mod === 1 ? false : true,
        email_primary: null,
        phone: '+34 600 000 000',
        daysAgo: n % 40,
      })
      it.latitude = lat
      it.longitude = lng
      out.push(it)
      n++
    }
  }
  BIG_CACHE = out
  return out
}

/**
 * Selección de demo para el bucle de fidelidad del mapa:
 *   - `?mapcase=onecity` → solo A Coruña (auto-encuadre de una sola ciudad).
 *   - `?mapcase=big` → cientos de puntos dispersos por España (volumen/pines).
 *   - resto → set completo disperso por Galicia.
 */
const GALICIA_CITIES = new Set(['A Coruña', 'Santiago', 'Vigo', 'Lugo'])

export function getDemoInbox(): InboxItem[] {
  if (typeof window !== 'undefined') {
    const c = new URLSearchParams(window.location.search).get('mapcase')
    if (c === 'onecity') return DEMO_INBOX.filter(i => i.address_components?.city === 'A Coruña')
    if (c === 'big') return bigDemoInbox()
    if (c === 'bigprovince')
      return bigDemoInbox().filter(i => i.address_components?.city === 'A Coruña')
    if (c === 'bigregion')
      return bigDemoInbox().filter(i => GALICIA_CITIES.has(i.address_components?.city ?? ''))
  }
  return DEMO_INBOX
}

export const DEMO_QUOTA: AccountQuota = {
  plan_code: 'pro',
  monthly_leads_quota: 800,
  monthly_leads_consumed: 612,
  monthly_leads_remaining: 188,
  daily_leads_consumed: 24,
  daily_leads_remaining: 216,
  period_end_at: new Date(Date.now() + 29 * DAY).toISOString(),
  day_resets_at: new Date(Date.now() + 0.5 * DAY).toISOString(),
}

/** Total acumulado ilustrativo para la cabecera (independiente de la muestra). */
export const DEMO_TOTAL_LEADS = 3482

export const DEMO_JOBS: UserJobSummary[] = [
  { id: 'j1', status: 'running', query: { categories: ['lawyer'], zone_query: { kind: 'admin_zone', country_code: 'ES', level: 'municipality', name: 'Ferrol' } }, result_listings_count: 48, total_count_estimate: 58, created_at: new Date(Date.now() - 0.08 * DAY).toISOString(), completed_at: null },
  { id: 'j2', status: 'completed', query: { categories: ['restaurant'], zone_query: { kind: 'admin_zone', country_code: 'ES', level: 'municipality', name: 'A Coruña' } }, result_listings_count: 132, total_count_estimate: 140, created_at: new Date(Date.now() - 2 * DAY).toISOString(), completed_at: new Date(Date.now() - 2 * DAY).toISOString() },
  { id: 'j3', status: 'completed', query: { categories: ['dentist'], zone_query: { kind: 'admin_zone', country_code: 'ES', level: 'municipality', name: 'Vigo' } }, result_listings_count: 87, total_count_estimate: 90, created_at: new Date(Date.now() - 8 * DAY).toISOString(), completed_at: new Date(Date.now() - 8 * DAY).toISOString() },
  { id: 'j4', status: 'completed', query: { categories: ['auto_repair_shop'], zone_query: { kind: 'admin_zone', country_code: 'ES', level: 'municipality', name: 'Santiago' } }, result_listings_count: 64, total_count_estimate: 70, created_at: new Date(Date.now() - 15 * DAY).toISOString(), completed_at: new Date(Date.now() - 15 * DAY).toISOString() },
  { id: 'j5', status: 'completed', query: { categories: ['real_estate_agency'], zone_query: { kind: 'admin_zone', country_code: 'ES', level: 'municipality', name: 'Lugo' } }, result_listings_count: 51, total_count_estimate: 55, created_at: new Date(Date.now() - 22 * DAY).toISOString(), completed_at: new Date(Date.now() - 22 * DAY).toISOString() },
  { id: 'j6', status: 'completed', query: { categories: ['hair_salon'], zone_query: { kind: 'admin_zone', country_code: 'ES', level: 'municipality', name: 'Ourense' } }, result_listings_count: 39, total_count_estimate: 42, created_at: new Date(Date.now() - 30 * DAY).toISOString(), completed_at: new Date(Date.now() - 30 * DAY).toISOString() },
  { id: 'j7', status: 'completed', query: { categories: ['gym'], zone_query: { kind: 'admin_zone', country_code: 'ES', level: 'municipality', name: 'A Coruña' } }, result_listings_count: 73, total_count_estimate: 80, created_at: new Date(Date.now() - 38 * DAY).toISOString(), completed_at: new Date(Date.now() - 38 * DAY).toISOString() },
  { id: 'j8', status: 'completed', query: { categories: ['lawyer'], zone_query: { kind: 'admin_zone', country_code: 'ES', level: 'municipality', name: 'Pontevedra' } }, result_listings_count: 96, total_count_estimate: 100, created_at: new Date(Date.now() - 45 * DAY).toISOString(), completed_at: new Date(Date.now() - 45 * DAY).toISOString() },
  { id: 'j9', status: 'completed', query: { categories: ['bakery'], zone_query: { kind: 'admin_zone', country_code: 'ES', level: 'municipality', name: 'Betanzos' } }, result_listings_count: 58, total_count_estimate: 60, created_at: new Date(Date.now() - 52 * DAY).toISOString(), completed_at: new Date(Date.now() - 52 * DAY).toISOString() },
]

export const DEMO_PLAN: SubscriptionPlan = {
  code: 'pro',
  name_es: 'Pro',
  name_en: 'Pro',
  monthly_price_eur: 25.95,
  monthly_leads_quota: 800,
  daily_quota_pct_limit: 30,
  single_job_max_leads: 200,
  single_job_confirm_threshold: 200,
  single_job_2fa_threshold: null,
  carry_over_leads: false,
  features: { export_csv: true, export_excel: true, presets_sectoriales: true },
  active: true,
  description_es: '',
  description_en: '',
}

/**
 * Pipeline de DEMO (bucle de fidelidad del kanban sin backend): reparte algunos
 * leads de DEMO_INBOX entre las 4 columnas con su `stage` y su seguimiento CRM
 * (próximo paso + estado de calor). Cubre los 4 estados visuales de la tarjeta
 * (vencido / hoy / futuro / sin próximo paso) para el bucle de fidelidad.
 */
const DEMO_STAGES: PipelineStage[] = ['nuevo', 'nuevo', 'contactado', 'nuevo', 'propuesta', 'contactado', 'cerrado', 'nuevo', 'propuesta', 'contactado', 'nuevo', 'cerrado']

/** Fecha local 'YYYY-MM-DD' desplazada `offset` días respecto a hoy. */
function ymd(offset: number): string {
  const d = new Date()
  d.setDate(d.getDate() + offset)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function nextStatus(dueOffset: number | null): NextStepStatus {
  if (dueOffset === null) return 'no_next_step'
  if (dueOffset < 0) return 'overdue'
  if (dueOffset === 0) return 'today'
  return 'future'
}
/** Próximo paso por índice: [texto, offset de días | null]. */
const DEMO_NEXT_STEPS: Array<[string, number | null]> = [
  ['Llamar para enseñar la auditoría', 0],   // hoy
  [null as unknown as string, null],          // sin próximo paso
  ['Seguir la llamada', -2],                  // vencido
  ['', 4],                                     // FECHA SIN TEXTO → "Próxima acción · +4 d"
  ['Revisar la propuesta enviada', 0],        // hoy
  ['Enviar email de seguimiento', 1],         // mañana
  ['Onboarding de la web', 7],                // futuro
  ['Llamar de nuevo', 3],                     // futuro
  ['Cierre por teléfono', 5],                 // futuro
  ['Confirmar reunión', -1],                  // vencido
  [null as unknown as string, null],          // sin próximo paso
  ['Pedir testimonio', 14],                   // futuro
]

// slice(2,…): deja fuera del pipeline los 2 leads de mayor oportunidad (d1/d2)
// para que, al abrir la ficha del top en preview, se vea el botón "Añadir".
export const DEMO_PIPELINE: PipelineCard[] = DEMO_INBOX.slice(2, 2 + DEMO_STAGES.length).map((it, i) => {
  const [text, off] = DEMO_NEXT_STEPS[i] ?? [null, null]
  return {
    ...it,
    stage: DEMO_STAGES[i],
    next_step_text: text ?? null,
    next_step_due: off === null ? null : ymd(off),
    next_step_status: nextStatus(off),
    created_at: daysAgoIso(6 - (i % 5)),
    updated_at: daysAgoIso(i % 3),
    has_email: !!it.email_primary,
    has_phone: !!it.phone,
    has_url: !!it.url,
  }
})

/**
 * Timeline de DEMO para la ficha (bucle de fidelidad del drawer sin backend):
 * mezcla notas manuales (editables) + eventos del sistema (solo lectura).
 */
export const DEMO_ACTIVITY: ActivityItem[] = [
  {
    id: 'act-1',
    place_id: DEMO_PIPELINE[0]?.place_id ?? 'demo',
    kind: 'note',
    activity_type: 'call',
    body: 'No contestaron, dejé recado con recepción. Reintentar por la tarde.',
    event_code: null,
    event_meta: null,
    created_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'act-2',
    place_id: DEMO_PIPELINE[0]?.place_id ?? 'demo',
    kind: 'note',
    activity_type: 'note',
    body: 'Reputación enorme (4,8★, 212 reseñas) y sin web propia. Ángulo: convertir reseñas en captación con una web.',
    event_code: null,
    event_meta: null,
    created_at: daysAgoIso(1),
    updated_at: daysAgoIso(1),
  },
  {
    id: 'act-3',
    place_id: DEMO_PIPELINE[0]?.place_id ?? 'demo',
    kind: 'event',
    activity_type: null,
    body: null,
    event_code: 'added_to_pipeline',
    event_meta: null,
    created_at: daysAgoIso(1),
    updated_at: daysAgoIso(1),
  },
]

// ============================================================================
// MÓDULO DE RESEÑAS — datos demo para el tablero + ángulo de venta (preview).
// Fotos: el placeholder de picsum sirve para el bucle de fidelidad; en prod
// las URLs son del CDN de Google (caducan → onError las oculta).
// ============================================================================

/**
 * Foto demo determinista como data-URI SVG (sin red → renderiza siempre,
 * también en el headless del bucle de fidelidad). En producción las fotos
 * son URLs reales del CDN de Google (y el onError las oculta si caducan).
 */
function demoPhoto(seed: string, w = 320, h = 240): string {
  // Hash simple del seed → tono estable.
  let h1 = 0
  for (let i = 0; i < seed.length; i++) h1 = (h1 * 31 + seed.charCodeAt(i)) % 360
  const c1 = `hsl(${h1} 55% 62%)`
  const c2 = `hsl(${(h1 + 40) % 360} 55% 48%)`
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}'>` +
    `<defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>` +
    `<stop offset='0' stop-color='${c1}'/><stop offset='1' stop-color='${c2}'/>` +
    `</linearGradient></defs><rect width='100%' height='100%' fill='url(#g)'/>` +
    `<circle cx='${w * 0.72}' cy='${h * 0.32}' r='${Math.min(w, h) * 0.16}' fill='rgba(255,255,255,.35)'/>` +
    `</svg>`
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}

/** Reseñas demo variadas: con/sin foto, con/sin respuesta del dueño, Local Guide. */
export const DEMO_REVIEWS: BusinessReview[] = [
  {
    review_id: 'rv-1',
    author_name: 'Marta Vázquez',
    author_profile_url: null,
    author_image_url: demoPhoto('marta', 96, 96),
    rating: 2,
    review_timestamp: daysAgoIso(6),
    time_ago: 'hace 6 días',
    text: 'La comida muy buena pero intenté reservar por internet y no tienen web ni forma de reservar online. Tuve que llamar tres veces hasta que cogieron el teléfono. Una pena porque el sitio lo merece.',
    original_text: null,
    language: 'es',
    owner_answer: 'Gracias por tu reseña Marta, estamos trabajando en mejorar las reservas.',
    owner_timestamp: daysAgoIso(5),
    photos: [demoPhoto('plato1'), demoPhoto('local1')],
    local_guide: true,
  },
  {
    review_id: 'rv-2',
    author_name: 'Diego Ferreiro',
    author_profile_url: null,
    author_image_url: null,
    rating: 1,
    review_timestamp: daysAgoIso(12),
    time_ago: 'hace 12 días',
    text: 'No contestan al teléfono nunca y la información de horarios en Google está mal: fui un martes y estaba cerrado. Deberían actualizar su ficha.',
    original_text: null,
    language: 'es',
    owner_answer: null,
    owner_timestamp: null,
    photos: [],
    local_guide: false,
  },
  {
    review_id: 'rv-3',
    author_name: 'Lucía Tojo',
    author_profile_url: null,
    author_image_url: demoPhoto('lucia', 96, 96),
    rating: 5,
    review_timestamp: daysAgoIso(20),
    time_ago: 'hace 20 días',
    text: 'Espectacular. El trato inmejorable y producto de primera. Volveré seguro.',
    original_text: null,
    language: 'es',
    owner_answer: null,
    owner_timestamp: null,
    photos: [demoPhoto('plato2'), demoPhoto('plato3'), demoPhoto('postre')],
    local_guide: true,
  },
  {
    review_id: 'rv-4',
    author_name: 'Roberto Sande',
    author_profile_url: null,
    author_image_url: null,
    rating: 3,
    review_timestamp: daysAgoIso(34),
    time_ago: 'hace 1 mes',
    text: 'Bien en general, aunque tardaron bastante en servir. Probé a buscar su web para ver la carta antes de ir y no encontré nada.',
    original_text: null,
    language: 'es',
    owner_answer: null,
    owner_timestamp: null,
    photos: [],
    local_guide: false,
  },
  {
    review_id: 'rv-5',
    author_name: 'Ana Pereira',
    author_profile_url: null,
    author_image_url: demoPhoto('ana', 96, 96),
    rating: 5,
    review_timestamp: daysAgoIso(48),
    time_ago: 'hace 2 meses',
    text: 'De lo mejorcito de la zona. Muy recomendable, repetiremos.',
    original_text: null,
    language: 'es',
    owner_answer: 'Mil gracias Ana, un placer teneros por aquí.',
    owner_timestamp: daysAgoIso(47),
    photos: [demoPhoto('local2')],
    local_guide: false,
  },
  {
    review_id: 'rv-6',
    author_name: 'Xoán Rodríguez',
    author_profile_url: null,
    author_image_url: null,
    rating: 4,
    review_timestamp: daysAgoIso(60),
    time_ago: 'hace 2 meses',
    text: 'Muy buena experiencia. Único pero: cuesta encontrarlos en Google, casi no aparecen.',
    original_text: null,
    language: 'es',
    owner_answer: null,
    owner_timestamp: null,
    photos: [],
    local_guide: false,
  },
]

/** Job demo ya completado (para el estado "listo" del tablero). */
export const DEMO_REVIEW_JOB: ReviewJob = {
  id: 'job-demo-reviews',
  place_id: DEMO_INBOX[0]?.place_id ?? 'demo',
  status: 'completed',
  depth: 50,
  sort_by: 'newest',
  reviews_count_total: DEMO_REVIEWS.length,
  reviews_stored: DEMO_REVIEWS.length,
  error_message: null,
  created_at: daysAgoIso(0),
  started_at: daysAgoIso(0),
  completed_at: daysAgoIso(0),
}

/** Ángulo de venta demo (heurística): web/reservas como tema dominante. */
// Ángulo v2 para un negocio SIN web: la señal objetiva `no_web` (kind
// 'objective', mentions 0) va PRIMERA, antes de las quejas de reseñas.
export const DEMO_ANGLE: SalesAngle = {
  place_id: DEMO_INBOX[0]?.place_id ?? 'demo',
  primary_code: 'web_reservas',
  primary_label: 'Vender web / reservas online',
  pitch:
    'Este negocio no tiene web propia y varias reseñas piden poder reservar ' +
    'online: oportunidad clara de venderle una web / sistema de reservas.',
  signals: [
    {
      code: 'no_web',
      label: 'El negocio no tiene web propia',
      mentions: 0,
      in_negative: 0,
      evidence: ['Dato del negocio: no consta web ni dominio propio.'],
      kind: 'objective',
    },
    {
      code: 'web',
      label: 'Web / reservas online',
      mentions: 3,
      in_negative: 2,
      evidence: [
        'intenté reservar por internet y no tienen web ni forma de reservar online',
        'probé a buscar su web para ver la carta antes de ir y no encontré nada',
      ],
      kind: 'review',
    },
    {
      code: 'phone',
      label: 'No contestan / contacto',
      mentions: 2,
      in_negative: 2,
      evidence: [
        'No contestan al teléfono nunca',
        'Tuve que llamar tres veces hasta que cogieron el teléfono',
      ],
      kind: 'review',
    },
    {
      code: 'visibility',
      label: 'Visibilidad / información desactualizada',
      mentions: 2,
      in_negative: 1,
      evidence: ['la información de horarios en Google está mal', 'cuesta encontrarlos en Google'],
      kind: 'review',
    },
  ],
  reviews_analyzed: DEMO_REVIEWS.length,
  negative_reviews: 3,
  avg_rating: 4.8,
  rating_votes_count: 96,
  version: 2,
  computed_at: daysAgoIso(0),
}

// Ángulo v2 para un negocio CON web: sin señal objetiva `no_web`; el ángulo
// sale de las reseñas (gestión de reputación). Para captar el caso "con web".
export const DEMO_ANGLE_WITH_WEB: SalesAngle = {
  place_id: DEMO_INBOX[3]?.place_id ?? 'demo',
  primary_code: 'reputacion_resenas',
  primary_label: 'Gestión de reputación / pedir reseñas',
  pitch:
    'Buena valoración pero pocas reseñas recientes y alguna queja de atención: ' +
    'oportunidad de gestión de reputación y captación de opiniones.',
  signals: [
    {
      code: 'service',
      label: 'Atención / servicio',
      mentions: 2,
      in_negative: 2,
      evidence: ['tardaron bastante en atender', 'trato mejorable en recepción'],
      kind: 'review',
    },
    {
      code: 'phone',
      label: 'No contestan / contacto',
      mentions: 1,
      in_negative: 1,
      evidence: ['cuesta que cojan el teléfono'],
      kind: 'review',
    },
  ],
  reviews_analyzed: 28,
  negative_reviews: 4,
  avg_rating: 4.9,
  rating_votes_count: 18,
  version: 2,
  computed_at: daysAgoIso(0),
}
